// src/rms/data/reportService.js
// ============================================================================
// Daily and monthly business reports
// ============================================================================
// Read from the ledger, never from documents. That is the whole reason the
// ledger exists: summing invoices at report time gives an answer that depends
// on which invoices somebody has since voided, edited, or raised late, and two
// runs of the same report disagree. Ledger entries are append-only and dated
// by the period they belong to, so a report run in June for March returns what
// it returned in April.
//
// ----------------------------------------------------------------------------
// Sign convention
// ----------------------------------------------------------------------------
// Every figure here is reported as a POSITIVE number in the direction a reader
// expects: revenue is positive when earned, deposits held positive when held.
// The ledger stores debits and credits, and a revenue account carries a credit
// balance, so revenue is (credits - debits) while an asset like cash is
// (debits - credits). Getting this backwards is the classic way to publish a
// report where a good month reads as a loss, so the direction comes from the
// account's own normal_balance rather than from a rule remembered per call
// site.
// ============================================================================

import db, { isLocalBackend } from "./index";
import { supabase } from "../config/supabaseClient";
import { createSuccessResponse, createErrorResponse } from "../utils/response";
import { ACCOUNTS } from "./glService";

/** Which side increases each account. Mirrors rms_gl_accounts.normal_balance. */
const NORMAL_BALANCE = {
  [ACCOUNTS.UNDEPOSITED]: "debit",
  [ACCOUNTS.RECEIVABLE]: "debit",
  [ACCOUNTS.DEPOSITS_HELD]: "credit",
  [ACCOUNTS.TAX_PAYABLE]: "credit",
  [ACCOUNTS.RENTAL_REVENUE]: "credit",
  [ACCOUNTS.ADDON_REVENUE]: "credit",
  [ACCOUNTS.DAMAGE_AND_FEES]: "credit",
  [ACCOUNTS.DEPOSITS_FORFEITED]: "credit",
  [ACCOUNTS.REFUNDS]: "debit",
};

const REVENUE_ACCOUNTS = [
  ACCOUNTS.RENTAL_REVENUE,
  ACCOUNTS.ADDON_REVENUE,
  ACCOUNTS.DAMAGE_AND_FEES,
  ACCOUNTS.DEPOSITS_FORFEITED,
];

/** Movement on an account over a set of entries, signed the way a reader reads it. */
function movement(entries, account) {
  const rows = entries.filter((e) => e.account_code === account);
  const debits = rows.reduce((s, e) => s + (e.debit_cents || 0), 0);
  const credits = rows.reduce((s, e) => s + (e.credit_cents || 0), 0);
  return NORMAL_BALANCE[account] === "debit" ? debits - credits : credits - debits;
}

/** yyyy-mm-dd for a Date, in UTC, matching how entry_date is written. */
const isoDate = (d) =>
  [d.getUTCFullYear(), String(d.getUTCMonth() + 1).padStart(2, "0"), String(d.getUTCDate()).padStart(2, "0")].join("-");

/**
 * Everything both reports are made of, for one date range.
 *
 * Split out because a daily report and a monthly one differ in period and in
 * whether they carry a per-day series -- not in what any of the figures mean.
 * Two implementations would eventually disagree about whether a forfeited
 * deposit is revenue, and the month would stop equalling the sum of its days.
 */
function summarise(entries) {
  const revenue = REVENUE_ACCOUNTS.map((code) => ({ account: code, cents: movement(entries, code) })).filter(
    (r) => r.cents !== 0
  );

  const grossRevenue = revenue.reduce((s, r) => s + r.cents, 0);
  const refunds = movement(entries, ACCOUNTS.REFUNDS);

  // Tax by rate, from tax_rate_id on the entry. This is the figure a return is
  // filed from, which is why it is broken out per authority rather than shown
  // as one number nobody can remit.
  const taxByRate = new Map();
  entries
    .filter((e) => e.account_code === ACCOUNTS.TAX_PAYABLE)
    .forEach((e) => {
      const key = e.tax_rate_id || "unattributed";
      const prior = taxByRate.get(key) || { tax_rate_id: e.tax_rate_id || null, name: e.memo || "Tax", cents: 0 };
      prior.cents += (e.credit_cents || 0) - (e.debit_cents || 0);
      taxByRate.set(key, prior);
    });

  return {
    revenue,
    gross_revenue_cents: grossRevenue,
    refunds_cents: refunds,
    net_revenue_cents: grossRevenue - refunds,

    tax_collected_cents: movement(entries, ACCOUNTS.TAX_PAYABLE),
    tax_by_rate: [...taxByRate.values()].filter((t) => t.cents !== 0),

    // Cash actually moved, which is not the same as revenue and is the figure
    // an operator counts against the till at close.
    cash_in_cents: movement(entries, ACCOUNTS.UNDEPOSITED),

    // Deposits are customers' money. Positive means more was taken than
    // returned; negative means the reverse. Never counted as revenue.
    deposits_movement_cents: movement(entries, ACCOUNTS.DEPOSITS_HELD),

    // Invoiced less collected. Positive means the business lent more than it
    // was paid this period.
    receivable_movement_cents: movement(entries, ACCOUNTS.RECEIVABLE),

    entry_count: entries.length,
  };
}

/**
 * Entries for a closed date range, inclusive at both ends.
 *
 * Against Supabase these arrive only in exchange for a PIN: rms_gl_entries has
 * no SELECT grant, and rms_ledger_entries checks the PIN, checks the role
 * behind it, and records that somebody read the accounts. Fetched on the PIN
 * rather than hidden behind it, so a modal in front of a loaded page is not
 * the only thing standing between a counter clerk and the books.
 *
 * The browser backend has no auth server and one synthetic user, so its PIN is
 * presentational -- the screen still asks, which is what keeps the flow
 * testable without signing in, but there is no second person to protect the
 * rows from.
 */
async function entriesBetween(operator, from, to, pin) {
  if (isLocalBackend) {
    const result = await db.list(
      "glEntries",
      { operator_id: operator.id, entry_date: { gte: from, lte: to } },
      { orderBy: "entry_date" }
    );
    return result.success ? result.data || [] : null;
  }

  const { data, error } = await supabase.rpc("rms_ledger_entries", {
    p_operator_id: operator.id,
    p_pin: pin,
    p_from: from,
    p_to: to,
  });
  if (error) return { error: error.message };
  return data || [];
}

/**
 * One day.
 *
 * `date` is a yyyy-mm-dd string rather than a Date, because a report for "the
 * 3rd" must mean the same day to an operator in Vancouver and a server in
 * UTC. Entry dates are calendar dates for exactly this reason.
 */
export async function dailyReport(operator, date, pin) {
  if (!operator?.id) return createErrorResponse("No business loaded.");
  const day = date || isoDate(new Date());

  const entries = await entriesBetween(operator, day, day, pin);
  if (entries === null) return createErrorResponse("The ledger could not be read.");
  if (entries.error) return createErrorResponse(entries.error);

  return createSuccessResponse({ period: "day", from: day, to: day, ...summarise(entries) });
}

/**
 * One calendar month, with the days inside it.
 *
 * The per-day series is built from the same entries as the totals, so the
 * chart and the summary cannot disagree -- a month whose days do not add up to
 * its total is the first thing an accountant notices and the last thing anyone
 * can explain.
 */
export async function monthlyReport(operator, year, month, pin) {
  if (!operator?.id) return createErrorResponse("No business loaded.");

  const now = new Date();
  const y = year || now.getUTCFullYear();
  const m = month || now.getUTCMonth() + 1;

  const first = new Date(Date.UTC(y, m - 1, 1));
  const last = new Date(Date.UTC(y, m, 0));
  const from = isoDate(first);
  const to = isoDate(last);

  const entries = await entriesBetween(operator, from, to, pin);
  if (entries === null) return createErrorResponse("The ledger could not be read.");
  if (entries.error) return createErrorResponse(entries.error);

  const byDay = new Map();
  entries.forEach((e) => {
    if (!byDay.has(e.entry_date)) byDay.set(e.entry_date, []);
    byDay.get(e.entry_date).push(e);
  });

  const days = [];
  for (let d = 1; d <= last.getUTCDate(); d += 1) {
    const key = isoDate(new Date(Date.UTC(y, m - 1, d)));
    const dayEntries = byDay.get(key) || [];
    const s = summarise(dayEntries);
    days.push({
      date: key,
      net_revenue_cents: s.net_revenue_cents,
      cash_in_cents: s.cash_in_cents,
      tax_collected_cents: s.tax_collected_cents,
    });
  }

  return createSuccessResponse({
    period: "month",
    year: y,
    month: m,
    from,
    to,
    days,
    ...summarise(entries),
  });
}

/** Human labels for the account codes a report shows. */
export const ACCOUNT_LABELS = {
  [ACCOUNTS.RENTAL_REVENUE]: "Rental revenue",
  [ACCOUNTS.ADDON_REVENUE]: "Add-on revenue",
  [ACCOUNTS.DAMAGE_AND_FEES]: "Damage and fees",
  [ACCOUNTS.DEPOSITS_FORFEITED]: "Deposits forfeited",
  [ACCOUNTS.REFUNDS]: "Refunds",
};

export default { dailyReport, monthlyReport, ACCOUNT_LABELS };
